import {
	faCircleCheck,
	faCircleExclamation,
	faLock,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
	PaymentLinkType,
	PublicPaymentLinkState,
} from '@supply-mover-2-0/shared';
import {
	logoForBrandSurface,
	resolveBrandPalette,
} from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX, ReactNode } from 'react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentPageContact, telHref } from '../../lib/supportContact';
import { usePortalSiteStore } from '../../stores/portalSiteStore';

/**
 * PD-16674 — the frame around the public payment-link pages: the brand's
 * header and a single centered column, with none of the signed-in storefront's
 * navigation (the visitor is not signed in and may never have an account).
 *
 * The header follows PortalHeader's rule for which wordmark to show: the
 * light-ink logo only when the header surface is itself light.
 *
 * While mounted it sets the page's referrer policy to `strict-origin`, so the
 * third-party scripts these pages load (the card fields, Turnstile) are only
 * ever told the storefront's origin, never the link in the address bar.
 */
export function PaymentLinkShell({
	children,
	secureLabel = 'Secure payment',
	showSignIn = true,
	wide = false,
}: {
	children: ReactNode;
	/** PD-13768 — a sent form's page says "Secure form". PD-17646 — null shows no label. */
	secureLabel?: string | null;
	/**
	 * PD-13768 — a sent form is usually signed by a new account or a lead with no
	 * storefront login, and the form is the whole task, so its page leaves the
	 * Sign in button out.
	 */
	showSignIn?: boolean;
	/** PD-17646 — booking pages lay a calendar beside the meeting's details. */
	wide?: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const navigate = useNavigate();
	const site = usePortalSiteStore((s) => s.site);

	useEffect(() => {
		const meta = document.createElement('meta');
		meta.name = 'referrer';
		meta.content = 'strict-origin';
		document.head.appendChild(meta);
		return (): void => {
			meta.remove();
		};
	}, []);

	const headerBg = resolveBrandPalette(
		{ primaryColor: site?.primaryColor ?? null },
		isDark ? 'dark' : 'light',
	).header;
	// Same logo choice as the storefront header (PortalHeader).
	const logoUrl = logoForBrandSurface(headerBg, {
		logoUrl: site?.logoUrl,
		logoUrlLight: site?.logoUrlLight,
	});
	const brandName = site?.displayName ?? null;
	const widthClass = wide ? 'max-w-5xl' : 'max-w-2xl';

	return (
		<div className="min-h-dvh bg-brand-page-bg flex flex-col">
			<header className="bg-brand-primary text-brand-primary-fg border-b border-brand-primary-fg/10 px-4 sm:px-6 pt-[env(safe-area-inset-top)]">
				<div className={`${widthClass} mx-auto h-16 flex items-center justify-between gap-4`}>
					{logoUrl != null && logoUrl !== '' ? (
						<img
							src={logoUrl}
							alt={t(brandName ?? 'Brand logo')}
							className="h-8 object-contain"
						/>
					) : (
						<span className="font-bold text-lg">{t(brandName ?? '')}</span>
					)}
					<div className="flex items-center gap-4">
						{secureLabel != null ? (
							<span className="flex items-center gap-2 text-sm text-brand-primary-fg/85">
								<FontAwesomeIcon icon={faLock} />
								<span className="hidden sm:inline">{t(secureLabel)}</span>
							</span>
						) : null}
						{/* Customers who have a storefront login can see every
						    invoice and their full balance there. The root route
						    shows sign-in to a visitor who is not signed in, as the
						    other public pages' Sign in buttons do. */}
						{showSignIn ? (
							<button
								type="button"
								onClick={() => {
									void navigate('/');
								}}
								className="text-sm font-medium text-brand-primary-fg border border-brand-primary-fg/40 rounded-md px-3 py-1.5 hover:bg-brand-primary-fg/10 transition-colors"
							>
								{t('Sign in')}
							</button>
						) : null}
					</div>
				</div>
			</header>
			<main className="flex-1 px-4 py-6 sm:py-10">
				<div className={`${widthClass} mx-auto`}>{children}</div>
			</main>
		</div>
	);
}

/**
 * The brand's billing phone and email (PD-17321), each falling back to the
 * support phone or email when the brand has no billing value for it; nothing
 * when the storefront has neither. These pages are about paying a bill or
 * sending a form, so a billing line, when the brand keeps one, is the more
 * useful one to show.
 */
export function PaymentLinkSupportLine(): JSX.Element | null {
	const { t } = useTranslation();
	const billing = usePortalSiteStore((s) => s.site?.billingContact ?? null);
	const support = usePortalSiteStore((s) => s.site?.supportContact ?? null);
	const contact = paymentPageContact(billing, support);
	if (contact == null) {
		return null;
	}
	return (
		<p className="text-sm text-brand-muted">
			{t('Questions? Contact us')}
			{contact.phone != null ? (
				<>
					{' '}
					<a
						href={telHref(contact.phone)}
						className="text-portal-accent hover:underline"
					>
						{t(contact.phone)}
					</a>
				</>
			) : null}
			{contact.phone != null && contact.email != null ? ` ${t('or')}` : null}
			{contact.email != null ? (
				<>
					{' '}
					<a
						href={`mailto:${contact.email}`}
						className="text-portal-accent hover:underline"
					>
						{t(contact.email)}
					</a>
				</>
			) : null}
			{t('.')}
		</p>
	);
}

const STATE_COPY: Record<
	Exclude<PublicPaymentLinkState, 'active'>,
	{ title: string; body: string }
> = {
	// Worded for what the link covers, never the whole account: the customer
	// may still owe other invoices this link does not include.
	paid: {
		title: 'Paid in full',
		body: 'Everything this link covers has been paid. Thank you.',
	},
	expired: {
		title: 'This link has expired',
		body: 'For your security, links only work for a limited time. Please contact us for a new one.',
	},
	revoked: {
		title: 'This link is no longer active',
		body: 'It has been withdrawn. Please contact us if you still need it.',
	},
	locked: {
		title: 'This link is locked',
		body: 'It was locked after several unsuccessful attempts, to protect your account. Please contact us for a new one.',
	},
	unavailable: {
		title: 'This is not available right now',
		body: 'Online payment is not available at the moment. Please contact us.',
	},
	invalid: {
		title: 'This link is not valid',
		body: 'Check that you opened the whole link from your email, or contact us for a new one.',
	},
};

/** A friendly, branded page for every state but active. */
/** The paid wording when the page knows what kind of link it is. */
const PAID_COPY_BY_TYPE: Partial<
	Record<PaymentLinkType, { title: string; body: string }>
> = {
	invoice: {
		title: 'This invoice is fully paid',
		body: 'Thank you for your payment.',
	},
	statement: {
		title: 'These invoices are fully paid',
		body: 'Every invoice this link covers has been paid. Thank you.',
	},
};

export function PaymentLinkStatePanel({
	state,
	linkType,
}: {
	state: Exclude<PublicPaymentLinkState, 'active'>;
	/** Known when the link loaded; picks the paid wording. */
	linkType?: PaymentLinkType;
}): JSX.Element {
	const { t } = useTranslation();
	const copy =
		state === 'paid' && linkType != null
			? (PAID_COPY_BY_TYPE[linkType] ?? STATE_COPY.paid)
			: STATE_COPY[state];
	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3">
			<FontAwesomeIcon
				icon={state === 'paid' ? faCircleCheck : faCircleExclamation}
				className={`text-4xl ${state === 'paid' ? 'text-success' : 'text-brand-muted'}`}
			/>
			<h1 className="text-2xl font-semibold text-default">{t(copy.title)}</h1>
			<p className="text-sm text-brand-muted">{t(copy.body)}</p>
			<div className="pt-2 flex justify-center">
				<PaymentLinkSupportLine />
			</div>
		</div>
	);
}
